import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ThemeProvider } from "@mui/material";
import theme from "../../theme";
import UserNavbarComponent from "../../components/Navbar/UserNavbarComponent";
import UserCatagoriesNavbar from "../../components/Navbar/UserCatagoriesNavbar";
import UserHeroSectionComponents from "../../components/Hero/UserHeroSectionComponents";
import UserHomeProductCardComponent from "../../components/Card/UserHomeProductCardComponent";
import SkeletonCardComponent from "../../components/Skeleton/SkeletonCardComponent";
import { GetProductAPI } from "../../api/product/ProductController";
import { _fetchMarketingCategoriesService } from "../../service/MasterDataService";
import NoDataComponent from "../../components/NoData/NoDataComponent";
import { GetShowPriceSettingAPI } from "../../api/settings/ShowPriceSettingController";

export default function HomePage({ history }) {
  const [selectedMarketingCategory, setSelectedMarketingCategory] = useState("Home");
  const [marketingCategoriesList, setMarketingCategoriesList] = useState([]);
  const [products, setProducts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showPrice, setShowPrice] = useState(true);
  const effectiveCategory = useMemo(
    () =>
      selectedMarketingCategory === "Home"
        ? marketingCategoriesList[0] || ""
        : selectedMarketingCategory,
    [selectedMarketingCategory, marketingCategoriesList]
  );
  const marketingCategoriesPreview = useMemo(
    () => marketingCategoriesList.slice(0, 4),
    [marketingCategoriesList]
  );

  // Fetch categories on initial load
  useEffect(() => {
    _fetchMarketingCategoriesService(setMarketingCategoriesList, setIsFetching);
  }, []);

  useEffect(() => {
    const fetchShowPrice = async () => {
      try {
        const businessName = process.env.REACT_APP_BUSINESS_NAME || "";
        if (!businessName) {
          return;
        }
        const data = await GetShowPriceSettingAPI(businessName);
        if (data?.baseResponseModel?.respCode === 200) {
          setShowPrice((data?.showPrice ?? 1) === 1);
        }
      } catch (error) {
        setShowPrice(true);
      }
    };
    fetchShowPrice();
  }, []);

  useEffect(() => {
    if (selectedMarketingCategory === "Home" && marketingCategoriesList.length > 0) {
      setSelectedMarketingCategory(marketingCategoriesList[0]);
    }
  }, [marketingCategoriesList, selectedMarketingCategory]);

  // Fetch products based on the selected marketing category
  const fetchProducts = useCallback(async () => {
    setIsFetching(true);
    const fetchCategory = effectiveCategory || "New Arrivals";
    const postBody = {
      product_Id: 0,
      Materials: [],
      color: [],
      category: "",
      marketingCategory: fetchCategory,
      MarketingCategory: fetchCategory,
      priceRange: "",
    };
    await GetProductAPI(postBody, setProducts, setIsFetching, true);
  }, [effectiveCategory]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filteredProducts = useMemo(() => {
    if (!effectiveCategory) {
      return products;
    }
    const normalizedCategory = String(effectiveCategory).toLowerCase();
    return products.filter((item) => {
      const productCategory =
        item?.marketingCategory ||
        item?.MarketingCategory ||
        item?.marketing_Category ||
        item?.marketingcategory;
      if (!productCategory) {
        return false;
      }
      return String(productCategory).toLowerCase() === normalizedCategory;
    });
  }, [effectiveCategory, products]);

  return (
    <ThemeProvider theme={theme}>
      <UserNavbarComponent history={history} />
      <UserHeroSectionComponents
        onShopClick={() => {
          const section = document.getElementById("home-categories");
          if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
      />
      <UserCatagoriesNavbar
        setSelectedCategory={setSelectedMarketingCategory}
        categoriesList={marketingCategoriesPreview}
        initialCategory={marketingCategoriesPreview[0]}
        showViewAll={marketingCategoriesList.length > 0}
        onViewAll={() =>
          history?.push ? history.push("/categories") : window.location.replace("/categories")
        }
      />
      {isFetching ? (
        console.log("Rendering SkeletonCardComponent") || (
          <SkeletonCardComponent />
        )
      ) : filteredProducts.length > 0 ? (
        <UserHomeProductCardComponent
          title=""
          data={filteredProducts}
          history={history}
          showPrice={showPrice}
        />
      ) : (
        <NoDataComponent />
      )}
    </ThemeProvider>
  );
}
